import React, { useState } from "react";
import { MetricCard } from "../ui/MetricCard";
import { FaArrowLeft, FaExternalLinkAlt } from "react-icons/fa";
import { Database, Eye } from "lucide-react";
import "./SubdomainDetail.scss";
import Tooltip from "@mui/material/Tooltip";
import axiosInstance from "../../../axiosInstanceSetup";
import ProductDetailModal from "./ProductDetailModal";
import heartLoading from "../../../assests/heartLoading.gif";
import ProductLogo from "../../ProductLogo/ProductLogo";
import LogoImage from "../../ProductLogo/LogoImage";
import { ShoppingCart, ShoppingBag } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import {
  ADD_TO_CART,
  REMOVE_FROM_CART,
  CLEAR_CART,
} from "../../../../src/redux/domainAddToCart/domainAddToCart.type";
import ERLandingPage from "../ERDiagram/pages/ERLandingPage";
import ProductModal from "../DataContract/ProductModal";

const serviceNowTicketLink =
  "https://marsprod.service-now.com/sp?id=incident_helper";

export const AssetsSummary = ({ subdomain, onBack, onL5Click, productId }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [showProductModal, setShowProductModal] = useState(false);
  const [productData, setProductData] = useState(null);
  const [loadingProduct, setLoadingProduct] = useState(false);
  const [productLoading, setProductLoading] = useState(false); 
  const [selectedProductForView, setSelectedProductForView] = useState(null)
  const [selectedAssets, setSelectedAssets] = useState([]);
  const dispatch = useDispatch();
  const cartItems = useSelector((state) => state.domainAddToCart);

  const mapAssetToCartItem = (asset) => ({
    id: asset.data_asset_id,
    name: asset.data_asset_name,
    productName: subdomain.product_name,
  });

  const isSelected = (assetId) => cartItems.some((item) => item.id === assetId);

  const rowsPerPage = 30;

  const filteredRows =
    subdomain?.product_assets_detail?.filter((sub) =>
      sub.data_asset_name.toLowerCase().includes(searchQuery.toLowerCase()),
    ) || [];

  const totalRows = filteredRows.length;
  const totalPages = Math.ceil(totalRows / rowsPerPage);

  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = filteredRows.slice(indexOfFirstRow, indexOfLastRow);

  const handleSelectAll = () => {
    const existingIds = new Set(cartItems.map((item) => item.id));

    const newItems = currentRows
      .filter((asset) => !existingIds.has(asset.data_asset_id))
      .map(mapAssetToCartItem);

    if (newItems.length) {
      dispatch({
        type: ADD_TO_CART,
        payload: newItems,
      });
    }
  };

  const handleRemoveAll = () => {
    const pageIds = currentRows.map((asset) => asset.data_asset_id);

    dispatch({
      type: REMOVE_FROM_CART,
      payload: pageIds,
    });
  };

  const handleSelectSingle = (asset) => {
    if (isSelected(asset.data_asset_id)) {
      dispatch({
        type: REMOVE_FROM_CART,
        payload: [asset.data_asset_id],
      });
    } else {
      dispatch({
        type: ADD_TO_CART,
        payload: [mapAssetToCartItem(asset)],
      });
    }
  };

  function getPartAfterPipe(str) {
    if (!str) return "";
    const parts = str.split("|");
    return parts.length > 1 ? parts[1] : str;
  }
const handleViewProduct = async () => {
  if (!subdomain?.product_name) return;
  setProductLoading(true);
  setShowProductModal(true);
  try {
    const response = await axiosInstance.get(
      `${process.env.REACT_APP_API_URL}/get-product-submission/${subdomain.product_name}`
    );
    if (response.status === 200) {
      setSelectedProductForView(response.data);
    }
  } catch (error) {
    console.error("Error fetching product details:", error);
  } finally {
    setProductLoading(false);
  }
};
  
  const handleCloseProductModal = () => {
    setShowProductModal(false);
    setProductData(null);
  };

  return (
    <div className="space-y-8">
      {/* Subdomain Summary */}
      <div className="rounded-lg p-4 text-white">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3 mb-4">
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h1 className="text-3xl font-bold text-black mb-4">
                {subdomain.product_name}
              </h1>
              <div>
                <div className="ml-1 inline-flex items-center gap-2 mb-4">
                  {subdomain.logoIds?.map((l) => (
                    <LogoImage key={l} logoId={l} />
                  ))}
                </div>
              </div>
            </div>

            
          </div>

          <div className="flex flex-col md:flex-row items-center space-x-3 gap-3 mt-2 md:mt-0 flex-shrink-0">
            <ERLandingPage />
            <a
              href={serviceNowTicketLink}
              target="_blank"
              rel="noopener noreferrer"
              className="h-[40px] text-[#bb1ccc] hover:text-white transition-colors duration-200 border border-[#bb1ccc] px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-[#bb1ccc] hover:shadow-lg whitespace-nowrap"
            >
              <FaExternalLinkAlt /> Raise an Incident
            </a>
            <button
              onClick={handleViewProduct}
              className="h-[40px] text-[#bb1ccc] hover:text-white transition-colors duration-200 border border-[#bb1ccc] px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-[#bb1ccc] hover:shadow-lg whitespace-nowrap"
            >
              <Eye className="h-5" />
              View Product
            </button>
            <button
              onClick={onBack}
              className="h-[40px] text-[#bb1ccc] hover:text-white transition-colors duration-200 border border-[#bb1ccc] px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-[#bb1ccc] hover:shadow-lg whitespace-nowrap"
            >
              <FaArrowLeft /> Go Back
            </button>
          </div>

         
        </div>
        <p className="text-lg mb-3 text-black mb-4 leading-relaxed">
              {subdomain.product_description}
            </p>

        {/* Summary Metrics */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <MetricCard
            title="Critical Data Elements"
            value={
              subdomain?.product_cde_count ? subdomain?.product_cde_count : "0"
            }
            variant="success"
          />
          <MetricCard
            title="Quality Score"
            value={subdomain?.product_score ? subdomain?.product_score : "0%"}
            variant="warning"
          />
          <MetricCard
            title="Total Assets"
            value={subdomain?.product_assets ? subdomain?.product_assets : "0"}
            variant="primary"
          />
        </div>
      </div>

      {/* L3 Sub-domains */}
      {subdomain &&
        subdomain.product_assets_detail &&
        subdomain.product_assets_detail.length > 0 && (
          <div className="space-y-6 p-2 ">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3">
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-1">
                <h2 className="text-2xl font-bold text-gray-900">
                  Assets Details
                </h2>
                <span className="text-sm text-gray-600">
                  ( Showing {indexOfFirstRow + 1} to{" "}
                  {Math.min(indexOfLastRow, totalRows)} of {totalRows} entries )
                </span>
              </div>
              <div className="flex items-center space-x-2 w-full md:w-auto">
                <input
                  type="text"
                  placeholder="Search Assets..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setCurrentPage(1); // Reset page when search updates
                  }}
                  className="w-full md:w-[300px] border border-gray-300 rounded px-3 py-1 text-sm h-[40px] focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 mt-1">
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 w-full">
                <button
                  className="px-3 py-1 rounded bg-[#1D7285] text-white whitespace-nowrap flex text-center items-center h-[37px]"
                  onClick={handleSelectAll}
                >
                  Select All On Page
                </button>
                <button
                  className="px-3 py-1 rounded bg-[#1D7285] text-white whitespace-nowrap flex text-center items-center h-[37px]"
                  onClick={handleRemoveAll}
                >
                  Remove All
                </button>
              </div>
            </div>

            {/* TABLE VIEW */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-sm font-medium text-black uppercase w-[20%]">
                        Asset Name
                      </th>
                      <th className="px-6 py-3 text-left text-sm font-medium text-black uppercase w-[60%]">
                        Description
                      </th>
                      <th className="px-6 py-3 text-left text-sm font-medium text-black uppercase w-[20%]">
                        Quality Score
                      </th>
                      <th className="px-6 py-3 text-left text-sm font-medium text-black uppercase w-[15%]">
                        Add to Cart
                      </th>
                    </tr>
                  </thead>

                  <tbody className="bg-white divide-y divide-gray-200">
                    {currentRows.map((l3Subdomain, index) => (
                      <tr
                        key={l3Subdomain.data_asset_id}
                        className={`cursor-pointer ${
                          index % 2 === 0 ? "bg-white" : "bg-gray-50"
                        } hover:bg-blue-50 transition-colors`}
                        onClick={() => onL5Click(l3Subdomain.data_asset_name)}
                      >
                        <td className="px-6 flex items-center gap-3 py-4 whitespace-nowrap w-[20%]">
                          <input
                            type="checkbox"
                            className="h-6 rounded border-gray-300 text-[#bb1ccc] focus:ring-[#bb1ccc]"
                            checked={isSelected(l3Subdomain.data_asset_id)}
                            onChange={() => handleSelectSingle(l3Subdomain)}
                            onClick={(e) => e.stopPropagation()}
                          />
                          <div className="flex items-center">
                            <span className="bg-[#3da046] w-[35px] h-[35px] rounded-xl flex items-center justify-center flex-shrink-0 mr-3">
                              <Database width="18px" color="white" />
                            </span>
                            <Tooltip
                              title={getPartAfterPipe(
                                l3Subdomain.data_asset_name,
                              )}
                              arrow
                              componentsProps={{
                                tooltip: {
                                  sx: {
                                    bgcolor: "black",
                                    color: "white",
                                    fontSize: "0.9rem",
                                    borderRadius: "6px",
                                  },
                                },
                                arrow: { sx: { color: "black" } },
                              }}
                            >
                              <span className="text-sm font-medium text-gray-900 cursor-help">
                                {getPartAfterPipe(l3Subdomain.data_asset_name)}
                              </span>
                            </Tooltip>
                          </div>
                        </td>

                        <td className="px-6 py-4 w-[60%]">
                          {l3Subdomain.asset_description ? (
                            <span className="text-sm text-gray-600 cursor-help">
                              {l3Subdomain.asset_description}
                            </span>
                          ) : (
                            <span className="text-sm text-gray-600">N/A</span>
                          )}
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap w-[20%]">
                          <div className="flex items-center">
                            <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                              <div
                                className={`h-2 rounded-full transition-all duration-300 ${
                                  parseFloat(l3Subdomain.asset_score) > 95
                                    ? "bg-green-600"
                                    : parseFloat(l3Subdomain.asset_score) < 90
                                      ? "bg-red-500"
                                      : "bg-yellow-500"
                                }`}
                                style={{
                                  width: `${parseFloat(
                                    l3Subdomain.asset_score,
                                  )}%`,
                                }}
                              />
                            </div>
                            <span className="text-sm font-medium text-gray-900">
                              {l3Subdomain.asset_score}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 w-[15%]">
                          <button
                            className="px-3 justify-center  py-1 rounded bg-[#1D7285] text-white whitespace-nowrap flex items-center h-[37px] min-w-[128px]"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSelectSingle(l3Subdomain);
                            }}
                          >
                            {isSelected(l3Subdomain.data_asset_id) ? (
                              <ShoppingBag className="h-5" />
                            ) : (
                              <ShoppingCart className="h-5" />
                            )}
                            <span className="ml-[8px] text-[12px]">
                              {isSelected(l3Subdomain.data_asset_id)
                                ? "Added"
                                : "Add to Cart"}
                            </span>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

      <div className="pagination flex flex-wrap md:flex-nowrap justify-between items-center gap-3 mt-4 px-4">
        <span className="text-sm text-gray-600">
          Showing {indexOfFirstRow + 1} to {Math.min(indexOfLastRow, totalRows)}{" "}
          of {totalRows} entries
        </span>
        <div className="pagination-controls flex flex-wrap gap-1">
          <button
            disabled={currentPage === 1}
            onClick={() => setCurrentPage((prev) => prev - 1)}
            className="px-2 py-1 border rounded disabled:opacity-50 hover:bg-gray-50 transition-colors duration-200"
          >
            Previous
          </button>
          {[...Array(totalPages)].map((_, idx) => (
            <button
              key={idx}
              className={`px-3 py-1 border rounded transition-colors duration-200 ${
                currentPage === idx + 1
                  ? "bg-blue-500 text-white"
                  : "hover:bg-gray-50"
              }`}
              onClick={() => setCurrentPage(idx + 1)}
            >
              {idx + 1}
            </button>
          ))}
          <button
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage((prev) => prev + 1)}
            className="px-2 py-1 border rounded disabled:opacity-50 hover:bg-gray-50 transition-colors duration-200"
          >
            Next
          </button>
        </div>
      </div>

     <ProductModal  showProductModal={showProductModal}  selectedProductForView={selectedProductForView}  productLoading={productLoading}  setShowProductModal={setShowProductModal}  setSelectedProductForView={setSelectedProductForView}/>
    </div>
    
  );
};

